/* --------------------------------------------------------------------------

  croner - MIT License - Hexagon <hexagon@56k.guru>

  ---------------------------------------------------------------------------

  MIT License

  Copyright (c) 2024 Rami Pellumbi

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
-----------------------------------------------------------------------------*/

import { type CronOptions, Cron as Croner } from "croner";

export class Cron {
    private job: Croner | null = null;

    constructor(
        private readonly cronExpression: string | Date,
        private readonly cronOptions?: CronOptions,
    ) {}

    /**
     * Starts the cron job with the specified handler.
     *
     * @param handler A function to be executed when the cron job triggers.
     * @throws {Error} If attempting to start a job that has already been started.
     */
    public start(handler: () => Promise<void> | void): void {
        if (this.job) {
            throw new Error("Attempting to start an already started job");
        }
        this.job = new Croner(this.cronExpression, this.cronOptions, handler);
    }

    /**
     * Stops the cron job. If the job is currently running, it will wait for the job to finish before stopping it.
     * This can be safely invoked even if the job hasn't been started.
     *
     * @param timeout The maximum time (in ms) to wait for the job to finish before stopping it forcefully.
     * @returns A promise that resolves when the job has been stopped
     */
    public stop(timeout: number): Promise<void> {
        return new Promise<void>((resolve) => {
            const startTime = Date.now();
            const checkAndStop = () => {
                if (!this.job) {
                    resolve(); // resolve if job has cleared
                    return;
                }

                if (this.job.isBusy()) {
                    if (Date.now() - startTime > timeout) {
                        this.job.stop();
                        this.job = null;
                        resolve();
                        return;
                    }
                    setTimeout(checkAndStop, 100);
                } else {
                    this.job.stop();
                    this.job = null;
                    resolve();
                    return;
                }
            };

            checkAndStop();
        });
    }

    /**
     * Triggers the cron job to run immediately. A triggered execution will prevent the job from running at its scheduled time
     * unless `preventOverlap` is set to `false` in the cron options.
     *
     * @throws {Error} If attempting to trigger a job that is not running.
     */
    public async trigger(): Promise<void> {
        if (!this.job) {
            throw new Error("Attempting to trigger a job that is not running");
        }
        await this.job.trigger();
    }
}
